import React, { ReactNode } from 'react';
import { useTabsContext } from './Tabs';
import { StyledTabButton, StyledTabTypography } from './Tabs.styles';

export interface TabProps {
  id: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

const Tab: React.FC<TabProps> = ({ id, children, disabled = false, className, ariaLabel }) => {
  const context = useTabsContext();

  if (!context) {
    throw new Error('Tab must be used within a Tabs component');
  }

  const { activeTab, setActiveTab, orientation, variant } = context;
  const isActive = activeTab === id;

  const handleClick = () => {
    if (!disabled) {
      setActiveTab(id);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <StyledTabButton
      role="tab"
      aria-selected={isActive}
      aria-disabled={disabled}
      aria-controls={`tabpanel-${id}`}
      aria-label={ariaLabel || `${children} tab`}
      id={`tab-${id}`}
      tabIndex={isActive ? 0 : -1}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      isActive={isActive}
      disabled={disabled}
      orientation={orientation}
      variant={variant}
    >
      <StyledTabTypography
        isActive={isActive}
        tabVariant={variant}
      >
        {children}
      </StyledTabTypography>
    </StyledTabButton>
  );
};

export default Tab;